import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';

import { getAppProps } from '@pages/_app';
import { buildDataUrl } from '@helper/api';
import TemplateDefault from '../../templates/default/default';

import Container from '../../layout/container/container';
import Headline from '../../atoms/headline/headline';
import Copy from '../../atoms/copy/copy';
import LongArrowRight from '../../icons/longArrowRight.svg';
import ExhibitionIntro from '../../molecules/exhibition-intro/exhibitionIntro';
import ExhibitionTeaser from '../../molecules/exhibition-teaser/exhibitionTeaser';
import Stage from '../../organisms/stage/stage';
import CuratedExhibitionTeasers from '../../organisms/curated-exhibition-teasers/curatedExhibitionTeasers';
import CuratedExhibitionSubHeadline from '../../organisms/curated-exhibition-sub-headline/curatedExhibitionSubHeadline';

import StyledExhibitionPage from './exhibition.style';

const Entry = ({
  data = {
    articleAuthor: null,
    typeTags: null,
    thinkingSpaces: null,
    curatedExhibitionTeasers: null,
    curatedExhibitionSubheadline: null
  }
}) => {
  const {
    thinkingSpaces,
    exhibitionIntro,
    exhibitionOutro,
    exhibitionImageText,
    curatedExhibitionTeasers,
    curatedExhibitionSubheadline
  } = data;

  return (
    <TemplateDefault invertedHeader noDotsFooter>
      <StyledExhibitionPage>
        <Stage />
        {exhibitionIntro && (
          <ExhibitionIntro
            {...exhibitionIntro}
            image={{
              alt: 'Exhibition Intro Image',
              lazy: 'default',
              src: '/static/images/exhibition-intro/exhibition-intro-2880w.jpg',
              srcset: [
                {
                  src: '/static/images/exhibition-intro/exhibition-intro-2880w.jpg',
                  suffix: '2880w'
                },
                {
                  src: '/static/images/exhibition-intro/exhibition-intro-1440w.jpg',
                  suffix: '1440w'
                },
                { src: '/static/images/exhibition-intro/exhibition-intro-720w.jpg', suffix: '720w' }
              ]
            }}
          />
        )}
        {exhibitionImageText && <ExhibitionTeaser {...exhibitionImageText} />}
        {curatedExhibitionTeasers && <CuratedExhibitionTeasers {...curatedExhibitionTeasers} />}
        {curatedExhibitionSubheadline && (
          <CuratedExhibitionSubHeadline headline={curatedExhibitionSubheadline} />
        )}
        {thinkingSpaces &&
          thinkingSpaces.length > 1 &&
          thinkingSpaces.map((item, index) => <ExhibitionTeaser key={index} {...item} />)}
        <div className="exhibition__outro">
          {exhibitionOutro && (
            <Container>
              <div className="exhibition__outro-wrapper">
                <Headline primary>{exhibitionOutro.headline}</Headline>
                <Link
                  href={{
                    pathname: '/universal',
                    query: { uri: exhibitionOutro.link.uri }
                  }}
                  as={exhibitionOutro.link.uri}
                  className="exhibition__outro-link"
                  title={exhibitionOutro.link.label}
                >
                  <LongArrowRight width="60px" height="20px" />
                  <Copy thick noMargin>
                    {exhibitionOutro.link.label}
                  </Copy>
                </Link>
              </div>
            </Container>
          )}
        </div>
      </StyledExhibitionPage>
    </TemplateDefault>
  );
};

Entry.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.string.isRequired,
    articleAuthor: PropTypes.arrayOf(PropTypes.shape()),
    typeTags: PropTypes.arrayOf(PropTypes.shape()),
    exhibitionIntro: PropTypes.shape().isRequired,
    exhibitionImageText: PropTypes.shape().isRequired,
    exhibitionOutro: PropTypes.shape().isRequired,
    curatedExhibitionTeasers: PropTypes.shape(),
    curatedExhibitionSubheadline: PropTypes.string,
    thinkingSpaces: PropTypes.arrayOf(PropTypes.shape())
  })
};

const getExhibitionProps = async ({ locale, uri }) => {
  const getPageProps = fetch(buildDataUrl(`${locale}/${process.env.NEXT_PUBLIC_JSON_API_PATH}/${uri}`))
    .then(res => res.json())
    .catch(err => {
      console.error('exhibition.jsx: ', err);
    });

  const [appProps, data] = await Promise.allSettled([getAppProps({ locale }), getPageProps]).then(
    results => results.map(result => result.value)
  );

  return {
    PAGE_TYPE: 'EXHIBITION',
    ...appProps,
    ...data
  };
};

export default Entry;
export { getExhibitionProps };
